import React, { useState } from 'react';
import { Mail, Key, Lock, User, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { apiService } from '../services/api';
import Modal from './ui/Modal';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ModalStep = 'email' | 'code' | 'userInfo' | 'success';

const inputClass =
  'w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all';

const primaryBtn =
  'w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-sm shadow-indigo-500/20 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:opacity-60 disabled:cursor-not-allowed transition-all';

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<ModalStep>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await apiService.generateRegistrationCode(email);
      if (result.success) {
        setStep('code');
      } else {
        setError(result.message || 'Failed to send registration email');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      setIsLoading(false);
      return;
    }
    try {
      const result = await apiService.verifyRegistrationCode(email, code);
      if (result.success) {
        setStep('userInfo');
      } else {
        setError(result.message || 'Invalid registration code');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    if (!firstName.trim()) {
      setError('First name is required');
      setIsLoading(false);
      return;
    }
    if (!password.trim()) {
      setError('Password is required');
      setIsLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }
    try {
      const result = await apiService.completeRegistration(
        email,
        code,
        password,
        firstName,
        lastName.trim() || undefined
      );
      if (result.success) {
        setStep('success');
      } else {
        setError(result.message || 'Failed to complete registration');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setStep('email');
    setEmail('');
    setCode('');
    setPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setFirstName('');
    setLastName('');
    setError('');
    setIsLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const spinner = <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white" />;

  const getTitle = () => {
    switch (step) {
      case 'email':
        return 'Create account';
      case 'code':
        return 'Enter verification code';
      case 'userInfo':
        return 'Complete your profile';
      case 'success':
        return 'Welcome!';
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'email':
        return (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="register-email" className="block text-sm font-semibold text-slate-700">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Mail className="w-4 h-4 text-slate-400" />
                </div>
                <input
                  id="register-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="your@email.com"
                />
              </div>
              <p className="text-xs text-slate-400">
                We'll send you a verification code to complete your registration.
              </p>
            </div>
            <button type="submit" disabled={isLoading} className={primaryBtn}>
              {isLoading ? spinner : 'Send verification code'}
            </button>
          </form>
        );

      case 'code':
        return (
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="register-code" className="block text-sm font-semibold text-slate-700">
                6-digit verification code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Key className="w-4 h-4 text-slate-400" />
                </div>
                <input
                  id="register-code"
                  type="text"
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className={`${inputClass} text-center text-lg tracking-widest`}
                  placeholder="123456"
                />
              </div>
              <p className="text-xs text-slate-400">Enter the 6-digit code sent to {email}</p>
            </div>
            <button type="submit" disabled={isLoading || code.length !== 6} className={primaryBtn}>
              {isLoading ? spinner : 'Verify code'}
            </button>
          </form>
        );

      case 'userInfo':
        return (
          <form onSubmit={handleUserInfoSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="first-name" className="block text-sm font-semibold text-slate-700">
                  First name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                  <input
                    id="first-name"
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={inputClass}
                    placeholder="John"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="last-name" className="block text-sm font-semibold text-slate-700">
                  Last name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                  <input
                    id="last-name"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={inputClass}
                    placeholder="Doe (optional)"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="register-password" className="block text-sm font-semibold text-slate-700">
                Password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="w-4 h-4 text-slate-400" />
                </div>
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="confirm-register-password" className="block text-sm font-semibold text-slate-700">
                Confirm password *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <Lock className="w-4 h-4 text-slate-400" />
                </div>
                <input
                  id="confirm-register-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputClass}
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button type="submit" disabled={isLoading} className={primaryBtn}>
              {isLoading ? spinner : 'Complete registration'}
            </button>
          </form>
        );

      case 'success':
        return (
          <div className="text-center space-y-4 py-2">
            <div className="mx-auto h-12 w-12 bg-emerald-50 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Registration successful!</h3>
            <p className="text-sm text-slate-500">
              Your account has been created. You can now sign in with your email and password.
            </p>
            <button
              onClick={handleClose}
              className="w-full flex justify-center py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm rounded-xl transition-all"
            >
              Close
            </button>
          </div>
        );
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} title={getTitle()} maxWidth="md">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm mb-4">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {renderStep()}
    </Modal>
  );
};

export default RegisterModal;
